import React, { useState } from 'react';
import { copyTextToClipboard } from '../../../lsc/copyToClipboard';
import type { RttsJobDetail } from '../types';

export type ShareCardProps = {
  job: RttsJobDetail;
  /** Fallback title when backend share_title is absent. */
  titleFallback?: string;
  className?: string;
};

/**
 * Richer share surface: copy-link works client-side today; OG/thumbnail slots
 * are laid out for a later server-rendered share asset.
 *
 * // BACKEND-DEP: richer share surface (og image / share payload)
 */
const ShareCard: React.FC<ShareCardProps> = ({
  job,
  titleFallback = 'Scoutlytics Studio reel',
  className,
}) => {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  // BACKEND-DEP: richer share surface (og image / share payload)
  const shareTitle = job.share_title || titleFallback;
  const shareUrl =
    job.share_url && job.share_url.startsWith('https://scoutlytics.gg/r/')
      ? job.share_url
      : '';
  const thumbnail = job.share_thumbnail_url || null;

  const onCopy = async () => {
    setCopied(false);
    setCopyFailed(false);
    if (!shareUrl) {
      setCopyFailed(true);
      return;
    }
    const ok = await copyTextToClipboard(shareUrl);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } else {
      setCopyFailed(true);
    }
  };

  return (
    <div
      className={[
        'rounded-xl border border-secondary-700 bg-secondary-900/50 overflow-hidden',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="rtts-share-card"
    >
      <div className="aspect-video bg-secondary-950 relative flex items-center justify-center">
        {thumbnail ? (
          <img
            src={thumbnail}
            alt=""
            className="h-full w-full object-cover"
            data-testid="rtts-share-thumbnail"
          />
        ) : (
          <div
            className="text-xs text-secondary-600 px-4 text-center"
            data-testid="rtts-share-thumbnail-placeholder"
          >
            {/* BACKEND-DEP: richer share surface (og image / share payload) */}
            Share preview coming soon
          </div>
        )}
      </div>
      <div className="p-3 space-y-2">
        <p className="text-sm font-medium text-white line-clamp-2" data-testid="rtts-share-title">
          {shareTitle}
        </p>
        {job.reel_duration_sec != null ? (
          <p className="text-[11px] text-secondary-500" data-testid="rtts-share-meta">
            Highlight reel
          </p>
        ) : (
          <p className="text-[11px] text-secondary-500">Highlight reel</p>
        )}
        <button
          type="button"
          className="btn-secondary text-xs"
          data-testid="rtts-share-copy"
          onClick={() => void onCopy()}
          disabled={!shareUrl}
        >
          {copied ? 'Link copied' : 'Copy link'}
        </button>
        {copyFailed ? (
          <p className="text-xs text-red-300" data-testid="rtts-share-copy-error">
            Could not copy link.
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default ShareCard;
